import { useEffect, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Popup, Tooltip } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Scan } from "@phosphor-icons/react";

export default function MapView() {
  const [stores, setStores] = useState([]);
  const [cams, setCams] = useState([]);
  const [quakes, setQuakes] = useState([]);
  const [analyzing, setAnalyzing] = useState(null);

  useEffect(() => {
    api.get("/stores").then((r) => setStores(r.data)).catch(() => {});
    api.get("/intel/cameras").then((r) => setCams(r.data)).catch(() => {});
    api.get("/intel/earthquakes").then((r) => setQuakes(r.data.quakes)).catch(() => {});
  }, []);

  const analyzeCam = async (cam) => {
    setAnalyzing(cam.id);
    try {
      const { data } = await api.post("/intel/analyze", { image_url: cam.snapshot_url });
      toast.success(`${cam.name}`, { description: data.result?.scene_summary?.slice(0, 120) });
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setAnalyzing(null); }
  };

  const center = stores.length ? [stores[0].lat, stores[0].lon] : [39.8, -98.5];

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-4 text-xs font-mono-data">
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#007AFF]" /> Stores ({stores.length})</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#32D74B]" /> Public Cameras ({cams.length})</span>
        <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#FF3B30]" /> Seismic (24h) ({quakes.length})</span>
        <span className="text-[#71717A]">Map: OpenStreetMap · public data</span>
      </div>

      <div className="border border-white/10 overflow-hidden" style={{ height: "70vh" }} data-testid="intel-map">
        <MapContainer center={center} zoom={4} style={{ height: "100%", width: "100%", background: "#0A0A0C" }} scrollWheelZoom>
          <TileLayer
            attribution='&copy; OpenStreetMap contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          {stores.map((s) => (
            <CircleMarker key={s.id} center={[s.lat, s.lon]} radius={9}
              pathOptions={{ color: "#007AFF", fillColor: "#007AFF", fillOpacity: 0.7 }}>
              <Tooltip>{s.name}</Tooltip>
              <Popup>
                <div style={{ minWidth: 140 }}>
                  <b>{s.name}</b><br />{s.city}<br />
                  <span style={{ fontSize: 11, color: "#666" }}>{s.lat.toFixed(3)}, {s.lon.toFixed(3)}</span>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {cams.map((c) => (
            <CircleMarker key={c.id} center={[c.lat, c.lon]} radius={6}
              pathOptions={{ color: "#32D74B", fillColor: "#32D74B", fillOpacity: 0.6 }}>
              <Popup>
                <div style={{ minWidth: 160 }}>
                  <b>{c.name}</b><br />
                  <span style={{ fontSize: 11, color: "#666" }}>{c.region}</span><br />
                  <img src={c.snapshot_url} alt={c.name} style={{ width: "100%", marginTop: 6, borderRadius: 2 }} />
                  <button onClick={() => analyzeCam(c)} disabled={analyzing === c.id}
                    style={{ marginTop: 6, width: "100%", padding: "6px", background: "#007AFF", color: "#fff", border: 0, cursor: "pointer", fontSize: 12, fontWeight: 700 }}>
                    {analyzing === c.id ? "Analyzing…" : "Analyze with AI"}
                  </button>
                </div>
              </Popup>
            </CircleMarker>
          ))}
          {quakes.filter((q) => q.mag != null && q.lat != null).map((q) => (
            <CircleMarker key={q.id} center={[q.lat, q.lon]} radius={Math.max(4, q.mag * 2)}
              pathOptions={{ color: "#FF3B30", fillColor: "#FF3B30", fillOpacity: 0.4 }}>
              <Popup>
                <div style={{ minWidth: 140 }}>
                  <b>M{q.mag?.toFixed(1)}</b> — {q.place}<br />
                  <span style={{ fontSize: 11, color: "#666" }}>{new Date(q.time).toLocaleString()}</span>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </MapContainer>
      </div>

      <p className="text-[11px] text-[#71717A] font-mono-data flex items-center gap-2">
        <Scan size={13} /> Click a green camera pin → "Analyze with AI" runs SentryVision's vision model on that public feed. Public directory is a sample — swap in your regional DOT feed URLs to go live.
      </p>
    </div>
  );
}
